import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

export type LineaCarrito = { presentacionId: string; cantidad: number }

type Estado = {
  lineas: LineaCarrito[]
  /** Unidades en total, para la insignia del encabezado. */
  unidades: number
  agregar: (presentacionId: string, cantidad: number) => void
  fijar: (presentacionId: string, cantidad: number) => void
  quitar: (presentacionId: string) => void
  vaciar: () => void
}

const CLAVE = 'rm-carrito-v1'
const Contexto = createContext<Estado | null>(null)

/** Lee el carrito guardado en este navegador; si no se puede (modo privado, datos dañados), empieza vacío. */
function leer(): LineaCarrito[] {
  try {
    const crudo = window.localStorage.getItem(CLAVE)
    if (!crudo) return []
    const datos: unknown = JSON.parse(crudo)
    if (!Array.isArray(datos)) return []
    return datos
      .filter((x): x is LineaCarrito => typeof x === 'object' && x !== null && typeof (x as LineaCarrito).presentacionId === 'string' && Number.isInteger((x as LineaCarrito).cantidad) && (x as LineaCarrito).cantidad > 0)
      .slice(0, 50)
  } catch {
    return []
  }
}

/**
 * Carrito de compras. Vive en el navegador: guarda solo qué presentación y cuántas unidades. Los precios y los
 * totales NUNCA salen de aquí; los calcula el servidor al crear el pedido.
 */
export function ProveedorCarrito({ children }: { children: ReactNode }) {
  const [lineas, setLineas] = useState<LineaCarrito[]>(() => leer())

  useEffect(() => {
    try {
      window.localStorage.setItem(CLAVE, JSON.stringify(lineas))
    } catch {
      /* sin almacenamiento: el carrito dura mientras la pestaña esté abierta */
    }
  }, [lineas])

  // Si se modifica desde otra pestaña, se sincroniza.
  useEffect(() => {
    const alCambiar = (e: StorageEvent) => {
      if (e.key === CLAVE) setLineas(leer())
    }
    window.addEventListener('storage', alCambiar)
    return () => window.removeEventListener('storage', alCambiar)
  }, [])

  const agregar = useCallback((presentacionId: string, cantidad: number) => {
    setLineas((ls) => {
      const existe = ls.find((l) => l.presentacionId === presentacionId)
      if (existe) return ls.map((l) => (l.presentacionId === presentacionId ? { ...l, cantidad: l.cantidad + cantidad } : l))
      return [...ls, { presentacionId, cantidad }].slice(0, 50)
    })
  }, [])
  const fijar = useCallback((presentacionId: string, cantidad: number) => {
    setLineas((ls) => ls.map((l) => (l.presentacionId === presentacionId ? { ...l, cantidad } : l)))
  }, [])
  const quitar = useCallback((presentacionId: string) => setLineas((ls) => ls.filter((l) => l.presentacionId !== presentacionId)), [])
  const vaciar = useCallback(() => setLineas([]), [])

  const valor = useMemo(
    () => ({ lineas, unidades: lineas.reduce((n, l) => n + l.cantidad, 0), agregar, fijar, quitar, vaciar }),
    [lineas, agregar, fijar, quitar, vaciar],
  )
  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>
}

export function useCarrito(): Estado {
  const c = useContext(Contexto)
  if (!c) throw new Error('useCarrito debe usarse dentro de ProveedorCarrito')
  return c
}
